/**
 * AI APIs → Overview (A7Y-5559): one card per LLM API provider with its
 * month-to-date spend, the basis that figure rests on (billed, or our rate
 * table over the provider's token counts), the month's tokens and a monthly
 * spend chart. Totals cover every key and environment; the provider detail
 * page splits them. Subscription accounts (AI Quota) are out of scope.
 *
 * The API caches for 24h (source: KV_TTL_SEC, a7y-api audit_spend_cache.ts); Refresh
 * re-runs the query with `force`.
 */
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import type { AiApisOverviewResponse } from '@a7y/types';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';
import { ViewQueryButton } from '@/pages/audit-spend/ViewQueryButton';
import { formatInt, formatUsd2 } from '@/pages/audit-spend/format';
import { MonthlySpendChart } from './MonthlySpendChart';
import { CARD_CLASSES, EMPTY_PROVIDER_MESSAGE, PROVIDER_ORDER, providerInfo } from './meta';
import { ProviderGlyph } from './provider-meta';
import { BasisBadge, DataAge, ErrorBanner } from './shared';

const MONTHS = 12;

type ProviderOverview = AiApisOverviewResponse['providers'][number];

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-zinc-400">{label}</div>
      <div className="text-sm font-medium tabular-nums text-zinc-900 dark:text-zinc-100">
        {value}
      </div>
    </div>
  );
}

function ProviderCard({ provider }: { provider: ProviderOverview }) {
  const info = providerInfo(provider.provider);
  const hasRows = provider.monthly.some((m) => m.costBasis !== 'none');
  const hasTopUps = Object.values(provider.ledgerTopUpsUsdByMonth ?? {}).some((v) => v !== 0);
  const mtd = provider.monthToDate;

  return (
    <Link
      to={`/admin/ai-apis/providers/${provider.provider}`}
      data-testid={`provider-card-${provider.provider}`}
      className={`${CARD_CLASSES} block transition-colors hover:border-zinc-300 dark:hover:border-zinc-600`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <ProviderGlyph provider={provider.provider} />
          <span className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
            {info.label}
          </span>
        </div>
        <BasisBadge basis={mtd.costBasis} />
      </div>

      <div className="mt-4 flex items-baseline gap-2">
        <span className="text-3xl font-semibold tabular-nums text-zinc-900 dark:text-white">
          {formatUsd2(mtd.costUsd)}
        </span>
        <span className="text-xs text-zinc-500 dark:text-zinc-400">month to date</span>
      </div>

      <div className="mt-3 grid grid-cols-3 gap-3">
        <Stat label="Input tokens" value={formatInt(mtd.inputTokens)} />
        <Stat label="Output tokens" value={formatInt(mtd.outputTokens)} />
        <Stat label="Cache tokens" value={formatInt(mtd.cacheReadTokens + mtd.cacheWriteTokens)} />
      </div>

      <div className="mt-4">
        {hasRows ? null : (
          <p
            data-testid={`provider-empty-${provider.provider}`}
            className={`text-xs text-zinc-500 dark:text-zinc-400 ${hasTopUps ? 'mb-2' : 'py-6 text-center'}`}
          >
            {EMPTY_PROVIDER_MESSAGE}
          </p>
        )}
        {hasRows || hasTopUps ? (
          <MonthlySpendChart provider={provider} colorHex={info.colorHex} />
        ) : null}
        {provider.ledgerTopUpsUsdByMonth && (hasRows || hasTopUps) ? (
          <p className="mt-1 text-[11px] text-zinc-500 dark:text-zinc-400">
            Solid: our call log. Hatched: Ledger credit top-ups.
          </p>
        ) : null}
      </div>
    </Link>
  );
}

export default function OverviewPage() {
  const forceRef = useRef(false);
  const [refreshing, setRefreshing] = useState(false);
  const overview = useQuery({
    queryKey: ['ai-apis', 'overview', MONTHS],
    queryFn: () => api.getAiApisOverview({ months: MONTHS, force: forceRef.current }),
  });

  const refresh = async () => {
    forceRef.current = true;
    setRefreshing(true);
    try {
      await overview.refetch();
    } finally {
      forceRef.current = false;
      setRefreshing(false);
    }
  };

  const envelope = overview.data;
  const byProvider = new Map((envelope?.data.providers ?? []).map((p) => [p.provider, p]));

  return (
    <div className="px-4 py-0">
      <PageHeader title="AI APIs" subtitle="LLM API usage and spend by provider">
        <div className="flex items-center gap-3">
          <DataAge generatedAt={envelope?.generated_at ?? envelope?.data.generatedAt} />
          <button
            type="button"
            onClick={() => void refresh()}
            disabled={refreshing}
            className="inline-flex items-center gap-1 rounded-md border border-zinc-200 dark:border-zinc-700 px-2 py-1 text-xs text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 disabled:opacity-50"
          >
            <RefreshCw className={`h-3 w-3 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </PageHeader>

      {overview.isError ? (
        <ErrorBanner endpoint="/admin/ai-apis/overview" error={overview.error} />
      ) : null}

      {overview.isLoading ? (
        <div className="px-2 text-sm text-zinc-500">Loading provider spend…</div>
      ) : envelope ? (
        <>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {PROVIDER_ORDER.map((id) => {
              const provider = byProvider.get(id);
              return provider ? <ProviderCard key={id} provider={provider} /> : null;
            })}
          </div>
          <div className="mt-3 px-2">
            <ViewQueryButton query={envelope.query} />
          </div>
        </>
      ) : null}
    </div>
  );
}
